import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import CommonFormLayout from "../OptmisedCode/Form/CommonFormLayout";
import RolePermissionEditor from "./RolePermissionEditor";
import {
  fetchRolePermissionCatalog,
  fetchRolePermissions,
  modulesForApplication,
  permissionsFromAllowedKeys,
  rolesForApplication,
  saveRolePermissions,
} from "./rolePermissionShared";

const BASE_ROUTE = "/role-permissions/access";

export default function RolePermissionsForm({ mode }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const preselectedAppId = searchParams.get("application_id");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [applicationId, setApplicationId] = useState("");
  const [roleId, setRoleId] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});
  const [errors, setErrors] = useState({});

  const applications = catalog?.applications || [];
  const roles = useMemo(
    () => rolesForApplication(catalog || {}, applicationId),
    [catalog, applicationId]
  );
  const modules = useMemo(
    () => modulesForApplication(catalog || {}, applicationId),
    [catalog, applicationId]
  );

  const loadCatalog = useCallback(async () => {
    const data = await fetchRolePermissionCatalog();
    setCatalog(data);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setLoading(true);
      try {
        const data = await loadCatalog();

        if (isEdit && id) {
          const roleData = await fetchRolePermissions(id);
          if (cancelled) return;

          const rid = String(roleData.application_user_role_id || id);
          setRoleId(rid);

          const roleRow = flattenRoleFromCatalog(data, Number(rid));
          if (roleRow?.application_id) {
            setApplicationId(String(roleRow.application_id));
          }

          const perms = (roleData.permissions || []).map((p) => ({
            application_module_action_id: p.application_module_action_id,
            permission_allowed: Boolean(p.permission_allowed),
          }));
          setPermissions(perms);
        } else if (!cancelled) {
          if (preselectedAppId) {
            setApplicationId(String(preselectedAppId));
          }
          const allowed = new Set(data.allowed_action_keys || []);
          if (roleId) {
            setPermissions(permissionsFromAllowedKeys(roleId, allowed));
          }
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.message ||
              err.message ||
              "Failed to load role permissions."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, loadCatalog]);

  useEffect(() => {
    if (!isEdit && roleId && catalog) {
      setPermissions(
        permissionsFromAllowedKeys(roleId, catalog.allowed_action_keys || [])
      );
    }
  }, [roleId, catalog, isEdit]);

  useEffect(() => {
    const expanded = {};
    modules.forEach((m) => {
      const key = m.application_module_id || m.module_id;
      expanded[key] = true;
    });
    setExpandedModules(expanded);
  }, [modules]);

  const validate = () => {
    const next = {};
    if (!applicationId) next.application_id = "Application is required.";
    if (!roleId) next.application_user_role_id = "Role is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await saveRolePermissions(roleId, permissions);
      toast.success(isEdit ? "Permissions updated." : "Permissions saved.");
      navigate(BASE_ROUTE);
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Save failed."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonFormLayout
      mode={mode}
      loading={loading}
      btnLoading={saving}
      onSubmit={handleSave}
      size="wide"
      wrapInGrid={false}
      submitDisabled={!roleId}
      headerProps={{
        addMessage: "Add role permissions",
        updateMessage: "Role permissions",
        homeLink: BASE_ROUTE,
        homeText: "Role permissions",
      }}
    >
            <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
              <FormControl sx={{ minWidth: 220 }} size="small" disabled={isEdit}>
                <InputLabel id="app-label">Application</InputLabel>
                <Select
                  labelId="app-label"
                  label="Application"
                  value={applicationId}
                  onChange={(e) => {
                    setApplicationId(e.target.value);
                    if (!isEdit) {
                      setRoleId("");
                      setPermissions([]);
                    }
                  }}
                  error={Boolean(errors.application_id)}
                >
                  {applications.map((app) => (
                    <MenuItem key={app.application_id} value={String(app.application_id)}>
                      {app.application_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 220 }} size="small" disabled={isEdit}>
                <InputLabel id="role-label">Role</InputLabel>
                <Select
                  labelId="role-label"
                  label="Role"
                  value={roleId}
                  onChange={(e) => setRoleId(e.target.value)}
                  error={Boolean(errors.application_user_role_id)}
                >
                  {roles.map((role) => (
                    <MenuItem
                      key={role.application_user_role_id}
                      value={String(role.application_user_role_id)}
                    >
                      {role.user_role_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {(errors.application_id || errors.application_user_role_id) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.application_id || errors.application_user_role_id}
              </Alert>
            )}

            {!applicationId || !roleId ? (
              <Alert severity="info">
                Select an application and role to assign module actions.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  Allowed:{" "}
                  {permissions.filter((p) => p.permission_allowed).length}{" "}
                  action(s)
                </Typography>
                <RolePermissionEditor
                  modules={modules}
                  permissions={permissions}
                  onPermissionsChange={setPermissions}
                  expandedModules={expandedModules}
                  onExpandedModulesChange={setExpandedModules}
                />
              </>
            )}

    </CommonFormLayout>
  );
}

function flattenRoleFromCatalog(catalog, roleId) {
  const apps = catalog?.applications || [];
  const rolesByApplication = catalog?.roles_by_application || {};
  for (const app of apps) {
    const appId = app.application_id;
    const roles =
      rolesByApplication[appId] || rolesByApplication[String(appId)] || [];
    const found = roles.find((r) => r.application_user_role_id === roleId);
    if (found) {
      return { ...found, application_id: appId, application_name: app.application_name };
    }
  }
  return null;
}
